<script setup lang='ts'>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Location } from '@/types/products';

import Popover from './Popover.vue';

type MapProps = {
  svgUrl: string;
  activityTitle: string;
  city: string;
  streetAddress: string;
  howToGo: string;
  transport: string;
  gps: Location['gps'];
  price: string;
};

const props = defineProps<{
  navigationLinks: Array<{ name: string; href: string; imgSrc: string }>;
  mapData: MapProps;
}>();

const { t } = useI18n();

const mapWrapperRef = ref<HTMLElement | null>(null);
const mapLoaded = ref(false);
let observer: IntersectionObserver | null = null;

// Instance de la carte + promesse de création partagée,
// pour que toute mise à jour (watcher ou init) attende que la carte existe réellement.
// eslint-disable-next-line unused-imports/no-unused-vars
let mapInstance: any;
let mapReadyPromise: Promise<any> | null = null;

function defaultGps(): Location['gps'] {
  return { lat: 46.16, lon: -1.2 } as Location['gps'];
}

async function createMap(gps: Location['gps']) {
  // @ts-expect-error cannot find module
  const { TouristicMap } = await import('https://api.touristicmaps.com/lib/mod.mjs');
  if (!TouristicMap) {
    return null;
  }
  const element = new TouristicMap({
    center: [gps.lon, gps.lat],
    range: [0.05, 0.05],
    fit_interaction: 'off',
    zoom_interaction: 'on',
    pitch_rotate_interaction: 'off',
    pan_interaction: 'off',
  });
  mapWrapperRef.value?.appendChild(element);
  return element;
}

function ensureMap(gps: Location['gps']) {
  if (!mapReadyPromise) {
    mapReadyPromise = createMap(gps).then((instance) => {
      mapInstance = instance;
      return instance;
    });
  }
  return mapReadyPromise;
}

async function updateMarker(mapData: MapProps) {
  const gps = mapData.gps ?? defaultGps();

  // Attend que la carte soit prête, quelle que soit la fonction qui appelle updateMarker en premier.
  const instance = await ensureMap(gps);
  if (!instance) {
    return;
  }

  const svg = await fetch(`${mapData.svgUrl}/map_pin.svg`);
  const svgAsBlob = await svg.blob();
  const url = `${URL.createObjectURL(svgAsBlob)}#map_pin`;

  const marker = {
    lat: gps.lat,
    lon: gps.lon,
    city: mapData.city,
    title: mapData.activityTitle,
    fromLabel: t('cta.price'),
    price: mapData.price,
  };

  const model = {
    pins: [marker],
    default: {
      em_size: 3,
      svg_href: url,
      popup(pin: any) {
        const div = document.createElement('div');
        div.classList.add('popup_wrapper');
        div.innerHTML = `
          <h3 class="popup_title">${pin.title}</h3>
          <p class="popup_price" style="font-weight:var(--yb-text-bold);">${pin.fromLabel} <span>${pin.price}</span></p>
          <p class="popup_city">${pin.city}</p>
        `;
        div.style = 'background-color:white;color:var(--yb-color-primary);border-radius:var(--yb-radius-sm);padding:.5rem;text-align:center;font-size:var(--yb-text-sm);width:min(65vw, 385px);';
        return div;
      },
    },
  };

  instance.setMarkers(model, 0);
  instance.position({ center: [gps.lon, gps.lat] });
}

async function initMap() {
  if (mapLoaded.value)
    return;
  mapLoaded.value = true;
  await updateMarker(props.mapData);
}

// Ne met à jour le marker que si la carte a déjà été (ou est en train d'être) initialisée.
watch(
  () => props.mapData,
  (newMapData) => {
    if (!mapLoaded.value)
      return;
    updateMarker(newMapData);
  },
  { deep: true },
);

onMounted(() => {
  observer = new IntersectionObserver(
    async ([entry]) => {
      if (!entry.isIntersecting)
        return;

      await initMap();
      observer?.disconnect();
    },
    {
      rootMargin: '200px',
      threshold: 0.1,
    },
  );

  if (mapWrapperRef.value) {
    observer.observe(mapWrapperRef.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<template>
  <section id="tmaps" class="normal_spacing sized" aria-label="localisation de l'activité">
    <div class="map_header">
      <h2 class="h2_title">
        {{ t('content.meeting_point') }}
      </h2>
      <div class="navigation_action">
        <Popover :items="navigationLinks" :title="t('popover.go_to')">
          <template #button>
            <svg
              id="location" width="18" height="18" viewBox="0 0 21 21" stroke="var(--yb-color-black)"
              fill="transparent" xmlns="http://www.w3.org/2000/svg"
              style="vertical-align:-1px;margin-right:0.25rem;"
            >
              <path
                d="M17.2884 9.13859C17.2884 14.1386 10.6217 19.1386 10.6217 19.1386C10.6217 19.1386 3.95508 14.1386 3.95508 9.13859C3.95508 7.37048 4.65746 5.67479 5.9077 4.42455C7.15794 3.1743 8.85363 2.47192 10.6217 2.47192C12.3899 2.47192 14.0855 3.1743 15.3358 4.42455C16.586 5.67479 17.2884 7.37048 17.2884 9.13859Z"
                stroke-width="1.43697" stroke-linecap="round" stroke-linejoin="round"
              />
              <path
                d="M10.6217 11.6386C12.0025 11.6386 13.1217 10.5193 13.1217 9.13859C13.1217 7.75788 12.0025 6.63859 10.6217 6.63859C9.24103 6.63859 8.12174 7.75788 8.12174 9.13859C8.12174 10.5193 9.24103 11.6386 10.6217 11.6386Z"
                stroke-width="1.43697" stroke-linecap="round" stroke-linejoin="round"
              />
            </svg>
            <span style="margin:0;">
              {{ t('content.go_to') }}
            </span>
          </template>
        </Popover>
      </div>
    </div>
    <div id="map_wrapper" ref="mapWrapperRef" class="small_spacing">
      <div v-if="!mapLoaded" class="map_skeleton" />
    </div>
    <p v-if="mapData.streetAddress" class="text_content small_spacing">
      <span>{{ t('content.address') }}</span> {{ mapData.streetAddress }}
    </p>
    <p v-if="mapData.howToGo" class="text_content small_spacing">
      <span>{{ t('content.how_to_go') }}</span> {{ mapData.howToGo }}
    </p>
    <p v-if="mapData.transport" class="text_content" style="margin:0;">
      <span>{{ t('content.transport') }}</span> {{ mapData.transport }}
    </p>
  </section>
</template>

<style scoped>
.small_spacing {
  margin-bottom: var(--yb-spacing-sm);
}

.normal_spacing {
  margin-bottom: var(--yb-radius-base);
}

.h2_title {
  font-size: clamp(1.875rem, 1.5625rem + 0.7813vw, 2.1875rem);
  font-weight: var(--yb-text-bold);
}

.sized {
  min-height: 500px;
}

.text_content {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-light);
  line-height: var(--yb-text-lineheight);

  span {
    margin-right: 4px;
    font-weight: var(--yb-text-bold);
  }
}

.map_header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (width < 1024px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

#map_wrapper {
  container-name: map;
  container-type: inline-size;
  width: 100%;
  height: 300px;
  background-color: var(--yb-color-white);
  margin-top: 0.5rem;
  border-radius: var(--yb-card-radius);
  overflow: hidden;

  @media screen and (width >= 1024px) {
    height: 550px;
  }
}

.navigation_action {
  width: fit-content;
  padding-inline: 0.5rem;
  padding-block: 0.25rem;
  border: 1px solid var(--yb-color-lightgray);
  margin-left: auto;
  cursor: pointer;
  border-radius: var(--yb-card-radius);

  #popover_control span {
    font-weight: var(--yb-text-bold);
  }

  &:hover {
    border: 1px solid var(--yb-color-primary);

    #popover_control svg {
      fill: var(--yb-color-primary);
    }

    #popover_control #location {
      fill: transparent;
      stroke: var(--yb-color-primary);
    }

    #popover_control span {
      color: var(--yb-color-primary);
    }
  }

  @media screen and (width < 1024px) {
    margin: 0.5rem 0px 0.25rem 0px;
  }
}

.popup_wrapper {
  color: var(--yb-color-primary);
  background-color: white;
  text-align: center;
  padding: 0.5rem;
  border-radius: var(--yb-radius-sm);
}

.popup_title {
  font-size: 0.85rem;
}

.popup_price {
  font-size: 0.75rem;
  color: var(--yb-color-black);

  span {
    font-weight: var(--yb-text-bold);
  }
}

.popup_city {
  font-size: 0.7rem;
  color: var(--yb-color-black);
}

.map_skeleton {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #f1f1f1 25%, #e7e7e7 50%, #f1f1f1 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}

@container map (width < 640px) {
  .popup_title {
    font-size: 0.7rem;
  }

  .popup_price {
    font-size: 0.65rem;
  }

  .popup_price {
    font-size: 0.6rem;
  }
}
</style>
